import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './LoginPage.css';
import './ForgotPasswordPage.css';
import { useBrand } from '../context/BrandContext';

function ForgotPasswordPage() {
  const { logoUrl: LOGO_URL, logoAlt: LOGO_ALT } = useBrand();
  const [step, setStep] = useState('request');
  const [userId, setUserId] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [maskedPhone, setMaskedPhone] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [infoMsg, setInfoMsg] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [otpTimer, setOtpTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    setErrorMsg('');
  }, [userId, otp, newPassword, confirmPassword, step]);

  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else if (resendTimer === 0 && step === 'verify') {
      setCanResend(true);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [resendTimer, step]);

  useEffect(() => {
    let interval = null;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    } else if (otpTimer === 0 && step === 'verify') {
      setErrorMsg('OTP has expired. Please request a new one.');
      setOtp(['', '', '', '']);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [otpTimer, step]);

  const stepSubtitle = useMemo(() => {
    if (step === 'verify') {
      return maskedPhone
        ? `Enter the 4-digit OTP sent to ${maskedPhone}`
        : 'Enter the 4-digit OTP sent to your mobile.';
    }
    if (step === 'reset') {
      return 'Create a new password to regain access.';
    }
    if (step === 'success') {
      return 'Your password has been updated.';
    }
    return 'Enter your email or mobile number to receive a reset OTP.';
  }, [step, maskedPhone]);

  const handleUserIdChange = (value) => {
    const trimmed = value.trim();
    const numericLike = /^[\d\s()+-]*$/.test(trimmed) && !trimmed.includes('@');
    if (numericLike) {
      setUserId(trimmed.replace(/\D/g, '').slice(0, 10));
      return;
    }
    setUserId(trimmed);
  };

  const handleRequestOtp = async (event) => {
    event.preventDefault();
    setErrorMsg('');
    setInfoMsg('');
    if (!userId) {
      setErrorMsg('Please enter your email or phone number.');
      return;
    }

    setIsLoading(true);
    try {
      const fetchUrl = `${API_URL}/api/auth/forgot-password/request-otp`;
      const response = await fetch(fetchUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setStep('verify');
        setOtp(['', '', '', '']);
        setResendTimer(60);
        setOtpTimer(Number(data.expiresIn) || 300);
        setCanResend(false);
        setMaskedPhone(data.maskedPhone || '');
        setInfoMsg('OTP sent successfully.');
      } else {
        setErrorMsg(data.message || 'Unable to send OTP. Please try again.');
      }
    } catch (error) {
      console.error('❌ [FORGOT-PASSWORD] Request OTP error:', error);
      setErrorMsg('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < 3) {
        const nextInput = document.getElementById(`reset-otp-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    setErrorMsg('');
    setInfoMsg('');
    const otpValue = otp.join('');
    if (otpValue.length !== 4) {
      setErrorMsg('Please enter the 4-digit OTP.');
      return;
    }

    setIsLoading(true);
    try {
      const fetchUrl = `${API_URL}/api/auth/forgot-password/verify-otp`;
      const response = await fetch(fetchUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId, otp: otpValue }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setStep('reset');
        setInfoMsg('OTP verified. Please set your new password.');
        setOtpTimer(0);
        setResendTimer(0);
        setCanResend(false);
      } else {
        setErrorMsg(data.message || 'Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.error('❌ [FORGOT-PASSWORD] Verify OTP error:', error);
      setErrorMsg('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    setErrorMsg('');
    setInfoMsg('');

    if (newPassword.length < 8) {
      setErrorMsg('Password must be at least 8 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMsg('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      const fetchUrl = `${API_URL}/api/auth/forgot-password/reset`;
      const response = await fetch(fetchUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId, otp: otp.join(''), newPassword }),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setStep('success');
        setInfoMsg('Password updated successfully.');
      } else {
        setErrorMsg(data.message || 'Failed to reset password. Please try again.');
      }
    } catch (error) {
      console.error('❌ [FORGOT-PASSWORD] Reset password error:', error);
      setErrorMsg('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-form-section">
          <Link to="/login" className="login-logo">
            <img src={LOGO_URL} alt={`${LOGO_ALT} Logo`} />
          </Link>

          <div className="login-content">
            <h1>Reset Password</h1>
            <p className="login-subtitle">{stepSubtitle}</p>

            {errorMsg && (
              <div className="login-error-container" style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid #ef4444',
                color: '#ef4444',
                padding: '0.8rem',
                borderRadius: '8px',
                marginBottom: '1.5rem',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                animation: 'shake 0.5s ease-in-out'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                {errorMsg}
              </div>
            )}

            {infoMsg && (
              <div className="reset-info">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M9 12l2 2 4-4"></path>
                </svg>
                {infoMsg}
              </div>
            )}

            <div className="reset-steps">
              <div className="reset-step-item">
                <div className={`reset-step ${step === 'request' ? 'active' : (step !== 'request' ? 'done' : '')}`}>1</div>
                <span className="reset-step-label">Identify</span>
              </div>
              <div className="reset-step-item">
                <div className={`reset-step ${step === 'verify' ? 'active' : (step === 'reset' || step === 'success' ? 'done' : '')}`}>2</div>
                <span className="reset-step-label">Verify</span>
              </div>
              <div className="reset-step-item">
                <div className={`reset-step ${step === 'reset' ? 'active' : (step === 'success' ? 'done' : '')}`}>3</div>
                <span className="reset-step-label">Reset</span>
              </div>
            </div>

            {step === 'request' && (
              <form className="auth-form" onSubmit={handleRequestOtp}>
                <div className="form-group">
                  <label htmlFor="reset-userId">Email or Phone Number</label>
                  <input
                    id="reset-userId"
                    type="text"
                    placeholder="Enter email or 10-digit mobile number"
                    value={userId}
                    onChange={(e) => handleUserIdChange(e.target.value)}
                    required
                  />
                  <p className="reset-note">We'll send an OTP to the mobile linked with this account.</p>
                </div>

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={!userId || isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner"></span> Sending OTP...
                    </>
                  ) : (
                    'Send OTP'
                  )}
                </button>

                <button
                  type="button"
                  className="change-number-btn"
                  onClick={() => setUserId('')}
                >
                  Clear
                </button>
              </form>
            )}

            {step === 'verify' && (
              <form className="auth-form" onSubmit={handleVerifyOtp}>
                <div className="form-group">
                  <label>Enter OTP</label>
                  <div className="otp-inputs">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        id={`reset-otp-${index}`}
                        type="text"
                        inputMode="numeric"
                        maxLength="1"
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Backspace' && !digit && index > 0) {
                            const prevInput = document.getElementById(`reset-otp-${index - 1}`);
                            if (prevInput) prevInput.focus();
                          }
                        }}
                      />
                    ))}
                  </div>
                  <div className="otp-footer">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
                      {otpTimer > 0 && (
                        <p className="timer-text" style={{
                          color: otpTimer <= 60 ? '#ef4444' : '#6b7280',
                          fontWeight: otpTimer <= 60 ? '600' : '400',
                          margin: 0
                        }}>
                          OTP expires in <span>{otpTimer}s</span>
                        </p>
                      )}
                      {resendTimer > 0 ? (
                        <p className="timer-text" style={{ margin: 0 }}>
                          Resend OTP in <span>{resendTimer}s</span>
                        </p>
                      ) : (
                        <button
                          type="button"
                          className="resend-btn"
                          onClick={handleRequestOtp}
                          disabled={!canResend || isLoading}
                        >
                          Resend OTP
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={otp.join('').length !== 4 || isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner"></span> Verifying...
                    </>
                  ) : (
                    'Verify OTP'
                  )}
                </button>

                <button
                  type="button"
                  className="change-number-btn"
                  onClick={() => {
                    setStep('request');
                    setOtp(['', '', '', '']);
                    setMaskedPhone('');
                    setOtpTimer(0);
                    setResendTimer(0);
                    setCanResend(false);
                  }}
                >
                  Change Email/Phone
                </button>
              </form>
            )}

            {step === 'reset' && (
              <form className="auth-form" onSubmit={handleResetPassword}>
                <div className="form-group">
                  <label htmlFor="new-password">New Password</label>
                  <div className="password-input-wrapper">
                    <input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter a new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                          <line x1="1" y1="1" x2="23" y2="23"></line>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      )}
                    </button>
                  </div>
                  <p className="reset-note">Use at least 8 characters.</p>
                </div>

                <div className="form-group">
                  <label htmlFor="confirm-password">Confirm Password</label>
                  <div className="password-input-wrapper">
                    <input
                      id="confirm-password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Re-enter your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                          <line x1="1" y1="1" x2="23" y2="23"></line>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={!newPassword || !confirmPassword || isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner"></span> Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </button>

                <button
                  type="button"
                  className="change-number-btn"
                  onClick={() => setStep('verify')}
                >
                  Back to OTP
                </button>
              </form>
            )}

            {step === 'success' && (
              <div className="reset-success">
                <div className="reset-success-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12l2 2 4-4"></path>
                    <circle cx="12" cy="12" r="10"></circle>
                  </svg>
                </div>
                <p>Your password has been updated. You can now sign in with your new password.</p>
                <Link to="/login" className="submit-btn reset-login-btn">Back to Login</Link>
              </div>
            )}
          </div>

          <p className="login-footer">
            Remembered your password? <Link to="/login">Sign in</Link>
          </p>
        </div>

        <div className="login-visual-section">
          <div className="visual-content">
            <div className="visual-feature-card">
              <h2>Secure Account Recovery</h2>
              <p>We use OTP verification to protect your account. Reset your password in minutes and get back to managing your finances.</p>
            </div>
          </div>

          <div className="visual-stats">
            <div className="stat-item">
              <h3>OTP Verified</h3>
              <p>SMS-secured access</p>
            </div>
            <div className="stat-item">
              <h3>24/7</h3>
              <p>Account support</p>
            </div>
            <div className="stat-item">
              <h3>100%</h3>
              <p>Encrypted passwords</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
